import React, { useId, useMemo, useRef, useState } from 'react';
import { ChevronDown, X } from 'lucide-react';
import { loadEntities } from '../../utils/shipmentsEntities';
import { useRemote } from './useRemote';
import { Field } from './FormFields';

const MAX_SHOWN = 50;
const plain = (s) => String(s || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

// Searchable picker (combobox + listbox) over an existing ERP list: customer | supplier | machine | order.
// value: numeric id or ''; onChange(id | '', item | null). `current` = { name } shown before the list is loaded.
const EntityPicker = ({ kind, label, value, onChange, current, hint, error, disabled = false, placeholder = 'Hledat…' }) => {
  const listId = useId();
  const wrapRef = useRef(null);
  const { data: items, error: loadError } = useRemote(kind, loadEntities);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);

  const selected = useMemo(() => (items || []).find((i) => i.id === Number(value)), [items, value]);
  const selectedLabel = value ? (selected?.label || current?.name || `#${value}`) : '';

  const shown = useMemo(() => {
    const needle = plain(query.trim());
    const all = items || [];
    return (needle ? all.filter((i) => plain(`${i.label} ${i.hint}`).includes(needle)) : all).slice(0, MAX_SHOWN);
  }, [items, query]);

  const choose = (item) => {
    onChange(item ? item.id : '', item || null);
    setOpen(false);
    setQuery('');
  };

  const openList = () => {
    if (disabled) return;
    setOpen(true);
    setActive(0);
  };

  const onKey = (e) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); if (!open) openList(); else setActive((a) => Math.min(a + 1, shown.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActive((a) => Math.max(a - 1, 0)); }
    else if (e.key === 'Enter' && open) { e.preventDefault(); if (shown[active]) choose(shown[active]); }
    else if (e.key === 'Escape' && open) { e.stopPropagation(); setOpen(false); setQuery(''); }
  };

  const onBlur = (e) => {
    if (!wrapRef.current?.contains(e.relatedTarget)) { setOpen(false); setQuery(''); }
  };

  return (
    <Field label={label} hint={hint} error={error}>
      {(aria) => (
        <div ref={wrapRef} className="shp-pop-wrap" onBlur={onBlur}>
          <input
            {...aria} className="shp-input" role="combobox" aria-expanded={open} aria-controls={listId} aria-autocomplete="list"
            aria-activedescendant={open && shown[active] ? `${listId}-${shown[active].id}` : undefined}
            autoComplete="off" disabled={disabled} placeholder={selectedLabel ? '' : placeholder}
            value={open ? query : selectedLabel}
            onClick={openList} onKeyDown={onKey}
            onChange={(e) => { setQuery(e.target.value); setActive(0); if (!open) setOpen(true); }}
            style={{ paddingRight: value ? 76 : 40 }}
          />
          <span style={{ position: 'absolute', right: 6, top: 4, display: 'flex', alignItems: 'center', gap: 2 }}>
            {value ? (
              <button type="button" className="shp-iconbtn" disabled={disabled} onClick={() => choose(null)} aria-label={`Zrušit výběr: ${label}`}>
                <X size={16} aria-hidden="true" />
              </button>
            ) : null}
            <ChevronDown size={18} color="#999" aria-hidden="true" style={{ marginRight: 8, pointerEvents: 'none' }} />
          </span>
          {open && (
            <ul id={listId} role="listbox" aria-label={label} tabIndex={-1} className="shp-pop shp-pop--inline" style={{ listStyle: 'none', margin: 0 }}>
              {loadError && <li className="shp-opt" role="alert">Seznam se nepodařilo načíst.</li>}
              {!loadError && !items && <li className="shp-opt" role="status">Načítám…</li>}
              {items && shown.length === 0 && <li className="shp-opt" role="status">Nic nenalezeno.</li>}
              {shown.map((item, idx) => (
                <li
                  key={item.id} id={`${listId}-${item.id}`} role="option" aria-selected={item.id === Number(value)}
                  className="shp-opt" data-active={idx === active}
                  onMouseDown={(e) => e.preventDefault()} onClick={() => choose(item)} onMouseEnter={() => setActive(idx)}
                >
                  <span className="shp-wrap">{item.label}</span>
                  {item.hint && <span className="shp-opt__hint shp-wrap">{item.hint}</span>}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </Field>
  );
};

export default EntityPicker;
